import { CellContext } from '@tanstack/react-table';

import { AdaptiveTableCell, Button, DynamicIconWrapper, Icons } from '@components/common';
import Actions from '@components/common/Actions';

import { IChangeMonitoring } from '@interfaces/networkElementConfigurations/networkElementConfigurations';

import colors from '@constants/colors';
import { columnSizes } from '@constants/table';
import {
	CHANGE_MONITORING_TABS_NAMES,
	EChangeMonitorActions,
	EChangeMonitorStatuses,
	TChangeMonitorAction,
} from './constants';

import ChangeMonitoringStatusCell from './ChangeMonitoringStatusCell';

import styles from './ChangeMonitoring.module.scss';
import tableStyles from '@components/common/Table/Table.module.scss';

interface IColumnProps {
	openHistoryModal: (item: IChangeMonitoring) => void;
	openErrorMessageModal: (item: IChangeMonitoring) => void;
	openStatusConfirmationModal: (item: IChangeMonitoring, action: TChangeMonitorAction) => void;
	openViewModal: (item: IChangeMonitoring) => void;
	openEditModal: (item: IChangeMonitoring) => void;
	openNoteModal: (item: IChangeMonitoring) => void;
	currentTab: string;
	isViewerOnly?: boolean;
}

export const getChangeMonitoringColumns = ({
	openHistoryModal,
	openErrorMessageModal,
	openStatusConfirmationModal,
	openViewModal,
	openEditModal,
	openNoteModal,
	currentTab,
	isViewerOnly,
}: IColumnProps) => {
	const handleView = (original: IChangeMonitoring) => () => openViewModal(original);
	const handleHistory = (original: IChangeMonitoring) => () => openHistoryModal(original);
	const handleEdit = (original: IChangeMonitoring) => () => openEditModal(original);

	const handleActionClick = (action: TChangeMonitorAction, original: IChangeMonitoring) => {
		return () => openStatusConfirmationModal(original, action);
	};

	return [
		{
			header: 'Parameter',
			accessorKey: 'parameterName',
			enableSorting: false,
			cell: ({ getValue }: CellContext<IChangeMonitoring, any>) => <AdaptiveTableCell text={getValue()} />,
		},
		{
			header: 'Value',
			accessorKey: 'parameterValue',
			enableSorting: false,
			maxSize: columnSizes.size100,
			cell: ({ getValue }: CellContext<IChangeMonitoring, any>) => {
				return (
					<AdaptiveTableCell
						maxTextWidth={columnSizes.size100}
						text={getValue()}
					/>
				);
			},
		},
		{
			header: 'Batch ID',
			accessorKey: 'groupNumber',
			cell: ({ row: { original } }: CellContext<IChangeMonitoring, any>) => {
				const { groupNumber, isUpdateNoteExist } = original;

				const handleOpenNotes = () => openNoteModal(original);

			return (
					<div className={styles.groupWrapper}>
						<span>{groupNumber?.toString()}</span>
						{isUpdateNoteExist && (
							<DynamicIconWrapper
								className={styles.noteIcon}
								icon={Icons.description}
								onClick={handleOpenNotes}
								tooltipLabel='View comment to changes'
							/>
						)}
					</div>
				);
			},
		},
		{
			header: 'Created',
			accessorKey: 'createdAt',
			cell: ({ row: { original } }: CellContext<IChangeMonitoring, any>) => {
				const { createdAtText } = original;

				return (
					<AdaptiveTableCell text={createdAtText} />
				);
			},
		},
		{
			header: 'User',
			accessorKey: 'userName',
			enableSorting: false,
			cell: ({ getValue }: CellContext<IChangeMonitoring, any>) => <AdaptiveTableCell text={getValue()} />,
		},
		{
			header: 'Status',
			accessorKey: 'status',
			enableSorting: false,
			cell: ({
				getValue, row: {
					original,
				},
			}: CellContext<IChangeMonitoring, any>) => {
				const handleErrorMessageClick = () => {
					openErrorMessageModal(original);
				};

				return (
					<ChangeMonitoringStatusCell
						openErrorMessageModal={handleErrorMessageClick}
						status={getValue()}
					/>
				);
			},
		},
		{
			header: 'Status Updated',
			accessorKey: 'updatedAt',
			cell: ({ row: { original } }: CellContext<IChangeMonitoring, any>) => {
				const { updatedAtText } = original;

				return (
					<AdaptiveTableCell text={updatedAtText} />
				);
			},
		},
		{
			header: () => <div className={tableStyles.centerColumnHeader}>Actions</div>,
			accessorKey: 'actions',
			enableSorting: false,
			size: columnSizes.size85,
			hidden: currentTab === CHANGE_MONITORING_TABS_NAMES.changeLog,
			cell: ({ row: { original } }: CellContext<IChangeMonitoring, any>) => {
				const status = original.status?.name;

				const isEdit = status === EChangeMonitorStatuses.CANDIDATE_ERROR
					|| status === EChangeMonitorStatuses.RUNNING_ERROR
					|| status === EChangeMonitorStatuses.CANDIDATE_ACCEPTED;
				const isTryAgain = status === EChangeMonitorStatuses.CANDIDATE_ERROR
					|| status === EChangeMonitorStatuses.RUNNING_ERROR;
				const isCancel = status === EChangeMonitorStatuses.CANDIDATE_ERROR
					|| status === EChangeMonitorStatuses.RUNNING_ERROR
					|| status === EChangeMonitorStatuses.CANDIDATE_ACCEPTED
					|| status === EChangeMonitorStatuses.CANDIDATE_PENDING && !original.isMiddlewareOnline
					|| status === EChangeMonitorStatuses.RUNNING_PENDING && !original.isMiddlewareOnline;
				const isSend = status === EChangeMonitorStatuses.CANDIDATE_ACCEPTED;

				return (
					<div className={styles.actionsWrapper}>
						<Actions
							editHandler={handleEdit(original)}
							gridNumberOfColumns={2}
							handleHistory={handleHistory(original)}
							historyTooltip='View history'
							isEdit={isEdit && !isViewerOnly}
							isHistory
							isView
							viewHandler={handleView(original)}
						/>
						{isTryAgain && !isViewerOnly && (
							<Button
								customColors={{
									background: colors.orange10,
									font: colors.orange70,
								}}
								onClick={handleActionClick(EChangeMonitorActions.TRY_AGAIN, original)}
								secondary
								text='Try again'
							/>)
						}
						{isSend && !isViewerOnly && (
							<Button
								customColors={{
									background: colors.green10,
									font: colors.green70,
								}}
								onClick={handleActionClick(EChangeMonitorActions.SEND_TO_RUNNING, original)}
								secondary
								text='Send to running'
							/>)
						}
						{isCancel && !isViewerOnly && (
							<Button
								customColors={{
									background: colors.pink10,
									font: colors.pink60,
								}}
								onClick={handleActionClick(EChangeMonitorActions.CANCEL, original)}
								secondary
								text='Cancel'
							/>)
						}
					</div>
				);
			},
		},
	];
};
